export type Platform = "telegram" | "max" | "browser";

let cached: Platform | null = null;

export function detectPlatform(): Platform {
  if (cached) return cached;
  const params = new URLSearchParams(window.location.search);

  if (params.get("source") === "max") return (cached = "max");
  if (params.get("source") === "twa") return (cached = "telegram");
  if (window.location.hash.includes("tgWebAppData")) return (cached = "telegram");
  if ((window as any).Telegram?.WebApp?.initData) return (cached = "telegram");
  if (window.location.hash.includes("WebAppData") && !window.location.hash.includes("tgWebAppData"))
    return (cached = "max");
  if ((window as any).WebApp?.initData && !(window as any).Telegram?.WebApp) return (cached = "max");
  return (cached = "browser");
}

let sdkLoaded = false;
export async function loadMessengerSdk(): Promise<void> {
  if (sdkLoaded) return;
  const platform = detectPlatform();
  if (platform === "browser") return;

  const src =
    platform === "max"
      ? "https://st.max.ru/js/max-web-app.js"
      : "https://telegram.org/js/telegram-web-app.js";

  await new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.onload = () => {
      sdkLoaded = true;
      resolve();
    };
    script.onerror = () => reject(new Error(`Failed to load SDK: ${src}`));
    document.head.appendChild(script);
  });
}

export function getInitData(): string | null {
  const platform = detectPlatform();
  if (platform === "telegram") return (window as any).Telegram?.WebApp?.initData || null;
  if (platform === "max") return (window as any).WebApp?.initData || null;
  return null;
}

/**
 * Дождаться initData после загрузки SDK.
 *
 * У Telegram она есть сразу и первый же вызов возвращает её — ожидание не наступает.
 * У MAX SDK доезжает asynchronously: сразу после onload `window.WebApp.initData` ещё пустая,
 * и одна синхронная проверка выдала бы «не получены данные инициализации» на ровном месте.
 * Поэтому опрашиваем — до таймаута, а не вечно: иначе экран «загрузка» висел бы навсегда.
 */
export async function waitForInitData(timeoutMs = 10_000): Promise<string | null> {
  const started = Date.now();
  for (;;) {
    const initData = getInitData();
    if (initData) return initData;
    if (Date.now() - started >= timeoutMs) return null;
    await new Promise((r) => setTimeout(r, 100));
  }
}

export function getWebApp(): any {
  const platform = detectPlatform();
  if (platform === "telegram") return (window as any).Telegram?.WebApp || null;
  if (platform === "max") return (window as any).WebApp || null;
  return null;
}

/**
 * Закрыть мини-приложение — устойчиво к капризам клиентов.
 *
 * Единственный best-effort `wa.close()` ненадёжен: на части клиентов Telegram первый вызов
 * проглатывается — в полноэкранном режиме (его включает authenticate() на мобильных через
 * requestFullscreen) и при гонке с только что запрошенным fullscreen. Поэтому:
 *  1) сначала выходим из fullscreen — иначе close на таких клиентах игнорируется;
 *  2) закрываем, а затем повторяем через короткие таймауты — на уже уничтоженном
 *     приложении повтор безвреден (этого кода в нём больше нет), а живое добьём;
 *  3) громко логируем, если WebApp недоступен — чтобы «окно не закрылось» было видно.
 */
export function closeWebApp(): void {
  const wa = getWebApp();
  if (!wa?.close) {
    console.warn("[closeWebApp] WebApp SDK недоступен — программно окно не закрыть");
    return;
  }

  try {
    if (wa.isFullscreen && typeof wa.exitFullscreen === "function") wa.exitFullscreen();
  } catch {
    // exitFullscreen нет на старых клиентах — не мешаем закрытию
  }

  const doClose = () => {
    try {
      wa.close();
    } catch (e) {
      console.error("[closeWebApp] close() бросил:", e);
    }
  };

  doClose();
  if (typeof setTimeout === "function") {
    setTimeout(doClose, 120);
    setTimeout(doClose, 400);
  }
}
